import type { CaseRecord, CaseSearchResult, EditorDraft, MeetingRecord } from './types';
import type { CreateCaseInput, CreateMeetingInput } from './repositories';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export const apiClient = {
  cases: {
    async get(projectId: string): Promise<CaseRecord | undefined> {
      const response = await fetch(`/api/cases/${encodeURIComponent(projectId)}`);
      if (response.status === 404) return undefined;
      return handleResponse<CaseRecord>(response);
    },

    async create(input: CreateCaseInput): Promise<CaseRecord> {
      const response = await fetch('/api/cases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return handleResponse<CaseRecord>(response);
    },

    async update(record: CaseRecord): Promise<void> {
      const response = await fetch(`/api/cases/${encodeURIComponent(record.projectId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(record),
      });
      await handleResponse<{ success: boolean }>(response);
    },

    async search(query: string, limit: number): Promise<CaseSearchResult[]> {
      const params = new URLSearchParams({ q: query, limit: String(limit) });
      const response = await fetch(`/api/cases/search?${params}`);
      return handleResponse<CaseSearchResult[]>(response);
    },
  },

  legacy: {
    async contracts(query: string, limit: number): Promise<CaseSearchResult[]> {
      const params = new URLSearchParams({ q: query, limit: String(limit) });
      const response = await fetch(`/api/legacy/contracts?${params}`);
      return handleResponse<CaseSearchResult[]>(response);
    },
  },

  meetings: {
    async get(meetingId: string): Promise<MeetingRecord | undefined> {
      const response = await fetch(`/api/meetings/${encodeURIComponent(meetingId)}`);
      if (response.status === 404) return undefined;
      return handleResponse<MeetingRecord>(response);
    },

    async create(input: CreateMeetingInput): Promise<MeetingRecord> {
      const response = await fetch('/api/meetings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return handleResponse<MeetingRecord>(response);
    },

    async update(record: MeetingRecord): Promise<void> {
      const response = await fetch(`/api/meetings/${encodeURIComponent(record.meetingId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(record),
      });
      await handleResponse<{ success: boolean }>(response);
    },

    async listByProject(projectId: string): Promise<MeetingRecord[]> {
      const response = await fetch(`/api/meetings/project/${encodeURIComponent(projectId)}`);
      return handleResponse<MeetingRecord[]>(response);
    },
  },

  drafts: {
    async get(draftId: string): Promise<EditorDraft | undefined> {
      const response = await fetch(`/api/drafts/${encodeURIComponent(draftId)}`);
      if (response.status === 404) return undefined;
      return handleResponse<EditorDraft>(response);
    },

    async update(record: EditorDraft): Promise<void> {
      const response = await fetch(`/api/drafts/${encodeURIComponent(record.draftId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(record),
      });
      await handleResponse<{ success: boolean }>(response);
    },
  },
};
